import React from 'react';
import useLang from '../../i18n/useLang';
import clients from '../site/clients';
import { SectionHeading } from '../site/PageParts';
import { Reveal, usePrefersReducedMotion } from '../site/motion';
import { PlcIcon, LineIcon, ErpIcon, ModelIcon } from './icons';

/* ---------- Client logos ---------- */

function LogoItem({ client, hidden }) {
  return (
    <li className="flex h-16 shrink-0 items-center justify-center px-7 sm:px-9">
      <img
        src={client.logo}
        alt={hidden ? '' : client.name}
        className="max-h-14 w-auto max-w-[140px] object-contain mix-blend-multiply grayscale contrast-125 transition-[filter] duration-300 hover:grayscale-0"
      />
    </li>
  );
}

// A moving row of client logos; under reduced motion it becomes a still grid with every logo visible.
export function LogoMarquee() {
  const { t } = useLang();
  const reduced = usePrefersReducedMotion();

  return (
    <section aria-labelledby="clients-title" className="py-14 sm:py-16">
      <Reveal as="h2" id="clients-title" className="text-center text-[0.95rem] font-medium text-ink-500">
        {t('home.logos.title')}
      </Reveal>

      {reduced ? (
        <ul className="wrap mt-8 grid grid-cols-2 items-center gap-y-6 sm:grid-cols-4">
          {clients.map((c) => (
            <LogoItem key={c.name} client={c} />
          ))}
        </ul>
      ) : (
        <div
          dir="ltr"
          className="group relative mt-8 flex overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]"
        >
          {/* Each copy holds the list twice so it stays wider than wide screens; the track moves one copy per loop. */}
          <div className="flex shrink-0 animate-marquee group-hover:[animation-play-state:paused] group-focus-within:[animation-play-state:paused]">
            <ul className="flex shrink-0 items-center">
              {[...clients, ...clients].map((c, i) => (
                <LogoItem key={`${c.name}-${i}`} client={c} hidden={i >= clients.length} />
              ))}
            </ul>
            <ul className="flex shrink-0 items-center" aria-hidden="true">
              {[...clients, ...clients].map((c, i) => (
                <LogoItem key={`${c.name}-${i}`} client={c} hidden />
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------- Platform (dark): what a system I build looks like, and how a project runs ---------- */

export function Platform() {
  const { t } = useLang();
  const p = (k) => t(`home.platform.${k}`);
  const layers = [
    { title: p('l1'), items: p('l1i'), icon: ErpIcon },
    { title: p('l2'), items: p('l2i'), icon: ModelIcon, own: true },
    { title: p('l3'), items: p('l3i'), icon: PlcIcon },
    { title: p('l4'), items: p('l4i'), icon: LineIcon },
  ];
  const steps = [1, 2, 3, 4, 5];

  return (
    <section className="relative isolate overflow-hidden bg-navy-900 py-24 text-white sm:py-32">
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-[linear-gradient(rgba(255,255,255,.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.04)_1px,transparent_1px)] [background-size:56px_56px] [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)]"
      />
      <div aria-hidden="true" className="drift absolute -top-40 start-1/3 -z-10 h-[34rem] w-[34rem] rounded-full bg-grovv/15 blur-3xl" />

      <div className="wrap">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-[1fr_1.1fr]">
          <div>
            <Reveal as="h2" variant="blur" className="heading max-w-[14ch] text-[2.25rem] sm:text-5xl lg:text-6xl">
              {p('title')}
            </Reveal>
            <Reveal as="p" delay={100} className="mt-6 max-w-[48ch] text-lg leading-relaxed text-navy-200 sm:text-xl">
              {p('lead')}
            </Reveal>
          </div>

          <div className="relative">
            <span aria-hidden="true" className="absolute inset-y-6 start-[2.15rem] w-px bg-gradient-to-b from-white/10 via-grovv/60 to-white/10" />
            <span
              aria-hidden="true"
              className="stack-packet absolute start-[2.15rem] z-10 h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-grovv shadow-[0_0_0_5px_rgba(97,196,53,.25)] rtl:translate-x-1/2"
            />
            <ol className="relative space-y-3">
              {layers.map((l, i) => (
                <Reveal
                  as="li"
                  key={l.title}
                  variant="end"
                  delay={(layers.length - 1 - i) * 140}
                  className={`relative flex items-center gap-4 rounded-3xl px-5 py-5 ring-1 ${
                    l.own ? 'glow-pulse bg-grovv text-ink ring-grovv-400' : 'bg-white/[.04] ring-white/10'
                  }`}
                >
                  <span
                    className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                      l.own ? 'bg-ink text-grovv' : 'bg-navy-800 text-white ring-1 ring-white/15'
                    }`}
                  >
                    <l.icon className="h-[18px] w-[18px]" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-lg font-semibold">{l.title}</span>
                    <span className={`mt-0.5 block text-[0.95rem] ${l.own ? 'text-ink-700' : 'text-navy-300'}`}>{l.items}</span>
                  </span>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>

        <Reveal as="h3" className="heading mt-24 text-2xl sm:text-3xl">
          {p('stepsTitle')}
        </Reveal>
        <ol className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-5 lg:gap-0">
          {steps.map((n, i) => (
            <Reveal as="li" key={n} delay={i * 110} className="lg:pe-6">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-grovv text-sm font-bold tabular-nums text-ink">{n}</span>
                <span aria-hidden="true" className={`hidden h-px flex-1 lg:block ${n === 5 ? 'bg-transparent' : 'bg-white/15'}`} />
              </div>
              <div className="text-xl font-semibold">{p(`st${n}`)}</div>
              <p className="mt-2 text-[0.98rem] leading-relaxed text-navy-200">{p(`st${n}b`)}</p>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ---------- Industries (Customers page) ---------- */

export function Industries() {
  const { t } = useLang();
  const tints = ['bg-mint-50', 'bg-sky-50', 'bg-fog-50', 'bg-sky-50', 'bg-fog-50', 'bg-mint-50'];
  return (
    <section className="wrap py-24 sm:py-32">
      <Reveal>
        <SectionHeading title={t('home.industries.title')} lead={t('home.industries.lead')} />
      </Reveal>
      <ul className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3, 4, 5, 6].map((n, i) => (
          <Reveal as="li" key={n} delay={i * 70} className={`flex min-h-[168px] flex-col justify-between rounded-4xl p-7 ${tints[i]}`}>
            <span className="heading text-2xl text-ink">{t(`home.industries.i${n}`)}</span>
            <span className="mt-6 text-[0.98rem] text-ink-500">{t(`home.industries.c${n}`)}</span>
          </Reveal>
        ))}
      </ul>
    </section>
  );
}
